// admin-tenant-login: types, normalisers and copy for "Log in as" and the impersonation audit.
// Import-free on purpose so `unit/adminImpersonation.spec.ts` runs in plain Node.

export type BlockedReason = 'TARGET_PLAN_BLOCKED' | 'TARGET_INACTIVE'

export interface ImpersonationMember {
  memberId: string
  userId: string
  name: string
  email: string
  role: number | null
  position: string | null
  isOwner: boolean
  blockedReason?: BlockedReason
}

export interface CursorPage<T> {
  items: T[]
  nextCursor: string | null
  hasNextPage: boolean
}

export interface ImpersonationMembersPage extends CursorPage<ImpersonationMember> {
  defaultMemberId: string | null
}

export interface ImpersonationStart {
  url: string
  expiresAt: string
}

export interface ImpersonationModeChange {
  at: string
  from: string
  to: string
  reason: string | null
  adminId: string | null
}

export interface ImpersonationAuditRow {
  _id: string
  companyId: string
  companyName: string | null
  actorAdminId: string | null
  actorAdminEmail: string | null
  targetMemberId: string | null
  targetName: string | null
  targetRole: number | null
  startedAt: string
  endedAt: string | null
  endReason: string | null
  reason: string | null
  ip: string | null
  modeChanges: ImpersonationModeChange[]
  writeModeEverEnabled: boolean
  durationMs: number | null
}

export interface AuditFilters {
  companyId: string | null
  adminId: string | null
  activeOnly: boolean
}

export const REASON_MIN = 10
export const REASON_MAX = 500
// The server clamps cursor lists to 1..100; one page covers almost every tenant, Load more handles the rest.
export const MEMBERS_PAGE_LIMIT = 100
export const AUDIT_PAGE_LIMIT = 25

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

// The backend sends `pageInfo.hasNextPage`; the plan text says `hasMore`. Either is read.
const readPageInfo = (v: unknown): { nextCursor: string | null; hasNextPage: boolean } => {
  const info = isObj(v) ? v : {}
  const nextCursor = str(info.nextCursor)
  const flag = typeof info.hasNextPage === 'boolean' ? info.hasNextPage : info.hasMore === true
  return { nextCursor, hasNextPage: flag && nextCursor !== null }
}

const readMember = (v: unknown): ImpersonationMember | null => {
  if (!isObj(v)) return null
  const memberId = str(v.memberId)
  if (!memberId) return null
  const blocked = v.blockedReason === 'TARGET_PLAN_BLOCKED' || v.blockedReason === 'TARGET_INACTIVE'
  return {
    memberId,
    userId: str(v.userId) ?? '',
    name: str(v.name) ?? '',
    email: str(v.email) ?? '',
    role: num(v.role),
    position: str(v.position),
    isOwner: v.isOwner === true,
    ...(blocked ? { blockedReason: v.blockedReason as BlockedReason } : {}),
  }
}

/** A body this cannot read is `null` (a failed load), never an empty list. */
export const normalizeMembersPage = (res: unknown): ImpersonationMembersPage | null => {
  if (!isObj(res) || !Array.isArray(res.items)) return null
  const items = res.items.map(readMember).filter((m): m is ImpersonationMember => m !== null)
  return { items, ...readPageInfo(res.pageInfo), defaultMemberId: str(res.defaultMemberId) }
}

export const normalizeStart = (res: unknown): ImpersonationStart | null => {
  if (!isObj(res)) return null
  const url = str(res.url)
  if (!url || !/^https?:\/\//i.test(url)) return null
  return { url, expiresAt: str(res.expiresAt) ?? '' }
}

const readModeChange = (v: unknown): ImpersonationModeChange | null => {
  if (!isObj(v)) return null
  const at = str(v.at)
  const from = str(v.from)
  const to = str(v.to)
  if (!at || !from || !to) return null
  return { at, from, to, reason: str(v.reason), adminId: str(v.adminId) }
}

const readAuditRow = (v: unknown): ImpersonationAuditRow | null => {
  if (!isObj(v)) return null
  const _id = str(v._id)
  const startedAt = str(v.startedAt)
  if (!_id || !startedAt) return null
  return {
    _id,
    companyId: str(v.companyId) ?? '',
    companyName: str(v.companyName),
    actorAdminId: str(v.actorAdminId),
    actorAdminEmail: str(v.actorAdminEmail),
    targetMemberId: str(v.targetMemberId),
    targetName: str(v.targetName),
    targetRole: num(v.targetRole),
    startedAt,
    endedAt: str(v.endedAt),
    endReason: str(v.endReason),
    reason: str(v.reason),
    ip: str(v.ip),
    modeChanges: Array.isArray(v.modeChanges)
      ? v.modeChanges.map(readModeChange).filter((c): c is ImpersonationModeChange => c !== null)
      : [],
    writeModeEverEnabled: v.writeModeEverEnabled === true,
    durationMs: num(v.durationMs),
  }
}

export const normalizeAuditPage = (res: unknown): CursorPage<ImpersonationAuditRow> | null => {
  if (!isObj(res) || !Array.isArray(res.items)) return null
  const items = res.items.map(readAuditRow).filter((r): r is ImpersonationAuditRow => r !== null)
  return { items, ...readPageInfo(res.pageInfo) }
}

// ── reason field ────────────────────────────────────────────────

export const validateReason = (value: string): true | string => {
  const length = value.trim().length
  if (length < REASON_MIN) return 'Enter at least 10 characters.'
  if (length > REASON_MAX) return 'Keep it under 500 characters.'
  return true
}

export const isReasonValid = (value: string): boolean => validateReason(value) === true

// ── errors ──────────────────────────────────────────────────────

export interface ApiFailure {
  status: number | null
  code: string | null
}

export const readFailure = (err: unknown): ApiFailure => {
  const response = isObj(err) && isObj(err.response) ? err.response : null
  const data = response && isObj(response.data) ? response.data : null
  const message = data ? data.message : null
  return {
    status: response ? num(response.status) : null,
    code: typeof message === 'string' ? message : Array.isArray(message) ? str(message[0]) : null,
  }
}

export const DISABLED_CODE = 'ADMIN_IMPERSONATION_DISABLED'

export const isFeatureDisabled = (f: ApiFailure): boolean => f.status === 404 && f.code === DISABLED_CODE

/** Members probe on company detail: only flag OFF hides Log in as; any other failure shows it and the dialog reports. */
export const logInAsVisibleAfterProbe = (err: unknown): boolean => !isFeatureDisabled(readFailure(err))

/** Audit page load failure: flag OFF gets its own empty state, anything else is a retryable failure. */
export const auditFailurePhase = (err: unknown): 'disabled' | 'failed' =>
  isFeatureDisabled(readFailure(err)) ? 'disabled' : 'failed'

export const START_TIMEOUT_MS = 15000
const TIMEOUT_CODE = 'ADMIN_IMPERSONATION_START_TIMEOUT'

/** Rejects with a timeout error when `promise` has not settled within `ms`. */
export const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(TIMEOUT_CODE)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

export const isTimeoutError = (err: unknown): boolean => err instanceof Error && err.message === TIMEOUT_CODE

export const POPUP_BLOCKED_MESSAGE = 'Your browser blocked the new tab. Allow pop-ups for this site and try again.'

export const startErrorMessage = (f: ApiFailure): string => {
  const lead = 'Could not start the session.'
  if (f.status === 429) return `${lead} Too many sessions started. Try again in a few minutes.`
  if (f.status === 401) return `${lead} Your admin session ended. Sign in again.`
  switch (f.code) {
    case 'TARGET_PLAN_BLOCKED':
      return `${lead} Only the Owner can be viewed on a Free plan.`
    case 'TARGET_INACTIVE':
    case 'TARGET_NOT_FOUND':
      return `${lead} This member is no longer active.`
    case 'COMPANY_NOT_FOUND':
      return `${lead} This company no longer exists.`
    case DISABLED_CODE:
      return `${lead} Log in as is turned off.`
  }
  if (f.status === 400) return `${lead} Check the reason and try again.`
  if (f.status === null) return `${lead} Check your connection and try again.`
  return `${lead} Try again.`
}

export const startFailureMessage = (err: unknown): string =>
  isTimeoutError(err)
    ? 'Could not start the session. The server did not answer in time. Try again.'
    : startErrorMessage(readFailure(err))

export const blockedReasonLabel = (reason: BlockedReason): string =>
  reason === 'TARGET_PLAN_BLOCKED' ? 'Free plan: only the Owner can be viewed' : 'Member is not active'

// ── audit display ───────────────────────────────────────────────

const ROLE_LABELS: Record<number, string> = { 1: 'Owner', 2: 'Admin', 3: 'Member', 4: 'Contractor' }

export const roleLabel = (role: number | null): string => (role === null ? '' : ROLE_LABELS[role] ?? '')

const MODE_LABELS: Record<string, string> = { readOnly: 'Read-only', write: 'Write' }

export const modeLabel = (mode: string): string => MODE_LABELS[mode] ?? mode

const END_REASON_LABELS: Record<string, string> = {
  user_exit: 'Exited',
  timeout: 'Timed out',
  absolute_expiry: 'Timed out',
  admin_logout: 'Admin logged out',
  logout_all: 'Admin logged out',
  logout_others: 'Admin logged out',
  logout: 'Signed out',
  admin_deleted: 'Admin removed',
  admin_perm_deleted: 'Admin removed',
  flag_off: 'Feature turned off',
  feature_disabled: 'Feature turned off',
  target_disabled: 'Member deactivated',
  reuse_detected: 'Ended for security',
  session_lost: 'Session lost',
  admin_revoked: 'Admin session ended',
  no_session: 'Session lost',
}

export const endReasonLabel = (reason: string | null): string => {
  if (!reason) return 'Ended'
  const known = END_REASON_LABELS[reason]
  if (known) return known
  const words = reason.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Ended'
}

export const isActiveRow = (row: ImpersonationAuditRow): boolean => row.endedAt === null

export const formatDuration = (ms: number): string => {
  const minutes = Math.max(0, Math.round(ms / 60000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} min` : `${hours} h`
}

/** Duration column: the stored duration, or the elapsed time "so far" for a live session. */
export const durationText = (row: ImpersonationAuditRow, nowMs: number): string => {
  const started = Date.parse(row.startedAt)
  if (isActiveRow(row)) return Number.isNaN(started) ? '' : `${formatDuration(nowMs - started)} so far`
  if (row.durationMs !== null) return formatDuration(row.durationMs)
  const ended = row.endedAt ? Date.parse(row.endedAt) : NaN
  return Number.isNaN(started) || Number.isNaN(ended) ? '' : formatDuration(ended - started)
}

// ── audit filters in the URL ────────────────────────────────────

export const readAuditFilters = (search: string): AuditFilters => {
  const params = new URLSearchParams(search)
  return {
    companyId: params.get('companyId') || null,
    adminId: params.get('adminId') || null,
    activeOnly: params.get('activeOnly') === 'true',
  }
}

export const auditFiltersToSearch = (filters: AuditFilters): string => {
  const params = new URLSearchParams()
  if (filters.companyId) params.set('companyId', filters.companyId)
  if (filters.adminId) params.set('adminId', filters.adminId)
  if (filters.activeOnly) params.set('activeOnly', 'true')
  const out = params.toString()
  return out ? `?${out}` : ''
}

export const hasAuditFilters = (filters: AuditFilters): boolean =>
  !!filters.companyId || !!filters.adminId || filters.activeOnly

export const auditQueryParams = (
  filters: AuditFilters,
  cursor: string | null
): Record<string, string | number | boolean> => ({
  limit: AUDIT_PAGE_LIMIT,
  ...(cursor ? { cursor } : {}),
  ...(filters.companyId ? { companyId: filters.companyId } : {}),
  ...(filters.adminId ? { adminId: filters.adminId } : {}),
  ...(filters.activeOnly ? { activeOnly: true } : {}),
})
